import { useState, useEffect, useRef } from "react";
import { trackPageview } from "@/lib/analytics";

// Aug 2026 — First-Party-Analytics: anonymer, cookieloser Pageview je Routenwechsel
function PageviewTracker() {
  const location = useLocation();
  useEffect(() => { trackPageview(); }, [location.pathname]);
  return null;
}
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { enforceSbDomain } from "@/utils/sbDomain";
import { isLexMode, isLexEnabled, isLexHost, clearLexMode, isLexUser as isLexUserFn, lexHostUrl, enforceLexDomain } from "@/utils/lexDomain";
import "@/styles/lex.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import TimerReminder from "@/components/TimerReminder";
import InactivityWarningModal from "@/components/InactivityWarningModal";
import TaxResidencyModal from "@/components/TaxResidencyModal";
import LocationChangeModal from "@/components/LocationChangeModal";
import OnboardingWizard from "@/components/OnboardingWizard";
import LexOnboardingWizard from "@/components/lex/LexOnboardingWizard";
import AVVConsentBanner from "@/components/AVVConsentBanner";
import ProfileCompletionBanner from "@/components/ProfileCompletionBanner";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import Footer from "@/components/Footer";
import MaintenancePage from "@/components/MaintenancePage";
import FloatingFeedbackButton from "@/components/FloatingFeedbackButton";
import ScrollToTop from "@/components/ScrollToTop";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import Dashboard from "@/pages/Dashboard";
import DesignPreview from "@/pages/DesignPreview";
import DashboardV2 from "@/pages/DashboardV2";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import TimeTracking from "@/pages/TimeTracking";
import Projects from "@/pages/Projects";
import StandardArticlesPage from "@/pages/StandardArticlesPage";
import Customers from "@/pages/Customers";
import GuestWelcome from "@/pages/GuestWelcome";
import GuestBanner from "@/components/GuestBanner";
import AreaGuide from "@/components/AreaGuide";
import GuestAreaLock from "@/components/GuestAreaLock";
import GuestReadOnlyBanner from "@/components/GuestReadOnlyBanner";
import GuestResume from "@/pages/GuestResume";
import Finances from "@/pages/Finances";
import Migration from "@/pages/Migration";
import MigrationHintModal from "@/components/migration/MigrationHintModal";
import Banking from "@/pages/Banking";
import Anlagen from "@/pages/Anlagen";
import Travel from "@/pages/Travel";
import MobileScan from "@/pages/MobileScan";
import Voranmeldung from "@/pages/Voranmeldung";
import Exports from "@/pages/Exports";
import Taxes from "@/pages/Taxes";
import Invoices from "@/pages/Invoices";
import Quotes from "@/pages/Quotes";
import Recurring from "@/pages/Recurring";
import QuoteAccept from "@/pages/QuoteAccept";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import GmailCallback from "@/pages/GmailCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ActivateAccount from "@/pages/ActivateAccount";
import VerifyEmail from "@/pages/VerifyEmail";
import Pricing from "@/pages/Pricing";
import UpgradeSuccess from "@/pages/UpgradeSuccess";
import Impressum from "@/pages/Impressum";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AVV from "@/pages/AVV";
import SBTerms from "@/pages/SBTerms";
import SBAvv from "@/pages/SBAvv";
import TaxDocumentation from "@/pages/TaxDocumentation";
import NewLanding from "@/pages/NewLanding";
import NewLandingOptimized from "@/pages/NewLandingOptimized";
import LexLanding from "@/pages/LexLanding";
import LexApprove from "@/pages/LexApprove";
import LexLogin from "@/pages/LexLogin";
import LexLegal from "@/pages/LexLegal";
import LexHonorar from "@/pages/LexHonorar";
import { LexAppFooter } from "@/components/lex/LexAppFooter";
import LexDashboard from "@/pages/LexDashboard";
import AdvisorLogin from "@/pages/AdvisorLogin";
import AdvisorDashboard from "@/pages/AdvisorDashboard";
import KanzleiPortal from "@/pages/KanzleiPortal";
import AdvisorBilling from "@/pages/AdvisorBilling";
import AdvisorClientDetail from "@/pages/AdvisorClientDetail";
import { AdvisorAuthProvider } from "@/contexts/AdvisorAuthContext";
import SteuerberaterTab from "@/pages/SteuerberaterTab";
import BuchhaltungOesterreich from "@/pages/seo/BuchhaltungOesterreich";
import BuchhaltungDeutschland from "@/pages/seo/BuchhaltungDeutschland";
import BuchhaltungSchweiz from "@/pages/seo/BuchhaltungSchweiz";
import LexofficeAlternative from "@/pages/seo/LexofficeAlternative";
import SevdeskAlternative from "@/pages/seo/SevdeskAlternative";
import RechnungsvorlageFreelancer from "@/pages/seo/RechnungsvorlageFreelancer";
import SteuerruecklagenFreelancer from "@/pages/seo/SteuerruecklagenFreelancer";
import SteuerruecklagenDeutschland from "@/pages/seo/SteuerruecklagenDeutschland";
import SteuerruecklagenOesterreich from "@/pages/seo/SteuerruecklagenOesterreich";
import SteuerruecklagenSchweiz from "@/pages/seo/SteuerruecklagenSchweiz";
import InvoiceGeneratorDE from "@/pages/seo/InvoiceGeneratorDE";
import InvoiceGeneratorAT from "@/pages/seo/InvoiceGeneratorAT";
import BelegerfassungFreelancer from "@/pages/seo/BelegerfassungFreelancer";
import ZeiterfassungFreelancer from "@/pages/seo/ZeiterfassungFreelancer";
import ZeiterfassungOesterreich from "@/pages/seo/ZeiterfassungOesterreich";
import ERechnungFreelancer from "@/pages/seo/ERechnungFreelancer";
import StundensatzRechner from "@/pages/seo/StundensatzRechner";
import QrRechnungGenerator from "@/pages/seo/QrRechnungGenerator";
import SteuerberaterPortal from "@/pages/seo/SteuerberaterPortal";
import { Clock, Briefcase, Users, Euro, Calculator, LayoutDashboard, Menu, X, FileText, Settings as SettingsIcon, ChevronLeft, ChevronRight, LogOut, CreditCard, Shield, Lock, Handshake, Banknote, ReceiptText, FileBox, Package, Boxes, FileSignature, Repeat, Plane } from "lucide-react";
// Feb 2026 — Sprint 1 D2: Was-ist-neu?-Changelog
import { ChangelogButton } from "@/components/changelog/ChangelogPanel";
import { GlobalTimerBar } from "@/components/TimerWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const queryClient = new QueryClient();

// Sub-Nav Map (P1a Feb 2026): kleine Tab-Bar unterhalb der Top-Nav,
// rendert nur wenn aktive Page einer Gruppe angehört.
const SUB_NAVS = {
  customers: [
    { path: "/customers", label: "Kunden", icon: Users },
    { path: "/projects", label: "Projekte", icon: Briefcase },
    { path: "/quotes", label: "Angebote", icon: FileSignature },
    { path: "/recurring", label: "Retainer & Abos", icon: Repeat },
    { path: "/standard-articles", label: "Standardartikel", icon: Package },
  ],
  finances: [
    { path: "/finances", label: "Übersicht", icon: Euro },
    { path: "/anlagen", label: "Anlagen", icon: Boxes },
    { path: "/reisen", label: "Reisen", icon: Plane },
    { path: "/banking", label: "Banking", icon: Banknote },
  ],
  taxes: [
    { path: "/taxes", label: "Steuern", icon: Calculator },
    { path: "/voranmeldung", label: "Voranmeldung", icon: ReceiptText },
    { path: "/exports", label: "Exporte", icon: FileBox },
  ],
};

// Reverse map: Pfad → Group, für Active-Highlight in der Top-Nav.
const PATH_TO_GROUP = Object.fromEntries(
  Object.entries(SUB_NAVS).flatMap(([group, items]) =>
    items.map((it) => [it.path, group])
  )
);

function SubNav() {
  const location = useLocation();
  const { user } = useAuth();
  // lex.ivoliq: keine Sub-Navigation — minimale Oberfläche für Anwälte
  if (isLexUserFn(user)) return null;
  const group = PATH_TO_GROUP[location.pathname];
  if (!group) return null;
  const items = SUB_NAVS[group];
  return (
    <div className="bg-white border-b border-zinc-200 shadow-sm" data-testid={`subnav-${group}`}>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-1 py-2.5 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {items.map((it) => {
            const Icon = it.icon;
            const active = location.pathname === it.path;
            return (
              <Link
                key={it.path}
                to={it.path}
                data-testid={`subnav-link-${it.path.replace('/', '')}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  active
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent'
                }`}
              >
                <Icon className="h-4 w-4" />
                {it.label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Navigation() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [missingReceipts, setMissingReceipts] = useState(0);
  const [sbUnreadCount, setSbUnreadCount] = useState(0);
  const { t, i18n } = useTranslation();
  const navScrollRef = useRef(null);
  const { user, logout, refreshUser } = useAuth();

  // lex (Sep 2026): Onboarding wieder aufnehmen — Skip-Flag löschen, Wizard erscheint
  const resumeLexOnboarding = async () => {
    try {
      await fetch(`${API}/lex/onboarding/skip`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
        credentials: "include",
        body: JSON.stringify({ skipped: false }),
      });
    } catch { /* ignore */ }
    refreshUser();
  };

  useEffect(() => {
    if (user) {
      const fetchStats = async () => {
        try {
          const res = await fetch(`${API}/expenses/receipt-stats`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
            credentials: "include",
          });
          if (res.ok) {
            const data = await res.json();
            setMissingReceipts(data.missing_receipt || 0);
          }
        } catch (e) { /* ignore */ }
      };
      const fetchSbUnread = async () => {
        try {
          // Combine legacy advisor unread (comments) + new collab inbox waiting_for_me
          const [legacyRes, collabRes] = await Promise.allSettled([
            fetch(`${API}/advisor/unread-count/${user.id}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
            }),
            fetch(`${API}/collab/inbox/freelancer`, {
              headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
            }),
          ]);
          let total = 0;
          if (legacyRes.status === 'fulfilled' && legacyRes.value.ok) {
            const d = await legacyRes.value.json();
            total += (d.unread || 0);
          }
          if (collabRes.status === 'fulfilled' && collabRes.value.ok) {
            const d = await collabRes.value.json();
            total += ((d.totals || {}).waiting_for_me || 0);
          }
          setSbUnreadCount(total);
        } catch { /* ignore */ }
      };
      fetchStats();
      fetchSbUnread();
    }
  }, [user]);
  
  // Helper function to translate plan names
  const getPlanDisplayName = (plan) => {
    if (!plan) return '';
    
    const planMap = {
      'free': t('pricing.plans.free.name') || 'Free',
      'basic': t('pricing.plans.basic.name') || 'Basic',
      'basic_monthly': t('pricing.plans.basic.name') || 'Basic',
      'basic_yearly': t('pricing.plans.basic.name') || 'Basic',
      'pro': t('pricing.plans.pro.name') || 'Pro',
      'pro_monthly': t('pricing.plans.pro.name') || 'Pro',
      'pro_yearly': t('pricing.plans.pro.name') || 'Pro',
      'lifetime': 'Lifetime'
    };
    
    return planMap[plan.toLowerCase()] || plan.replace('_', ' ');
  };
  
  const userPlan = user?.plan || 'free';
  const isFreePlan = userPlan === 'free' && !user?.has_lifetime_access;
  const isProPlan = user?.has_lifetime_access || ['pro', 'pro_monthly', 'pro_yearly', 'lifetime'].includes(userPlan);

  // P1a (Feb 2026) — Information Architecture: 8 Top-Items mit Sub-Tab-Bar.
  // Vorher: 12 Items flach. Jetzt: gruppiert nach „statisch / dynamisch / funktional"-Logik.
  // lex.ivoliq (Juni 2026): Anwälte sehen nur eine minimale Navigation.
  const isLexUser = isLexUserFn(user);
  const navItems = isLexUser ? [
    { path: "/lex", label: "Übersicht", icon: LayoutDashboard, tourId: "lex-dashboard" },
    { path: "/lex/honorar", label: "Honorarnoten", icon: Repeat, tourId: "lex-recurring" },
    { path: "/voranmeldung", label: "Voranmeldung", icon: ReceiptText, tourId: "lex-uva" },
    { path: "/settings", label: "Einstellungen", icon: SettingsIcon, tourId: "settings" },
  ] : [
    { path: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, tourId: "dashboard", locked: isFreePlan },
    { path: "/customers", label: "Kunden & Aufträge", icon: Users, tourId: "customers", group: "customers" },
    { path: "/time-tracking", label: "Zeit", icon: Clock, tourId: "time-tracking" },
    { path: "/invoices", label: "Rechnungen", icon: FileText, tourId: "invoices" },
    { path: "/finances", label: "Finanzen", icon: Euro, tourId: "finances", group: "finances" },
    { path: "/taxes", label: "Steuern", icon: Calculator, tourId: "taxes", locked: !isProPlan, group: "taxes" },
    { path: "/steuerberater-bereich", label: "Steuerberater", icon: Handshake, tourId: "steuerberater", locked: !isProPlan },
    { path: "/settings", label: "Einstellungen", icon: SettingsIcon, tourId: "settings" },
  ];

  // Add admin link if user is admin
  const ADMIN_EMAIL = "wilhelm.wagner.consulting@gmail.com";
  if (user && user.email === ADMIN_EMAIL) {
    navItems.push({ 
      path: "/admin", 
      label: "Admin", 
      icon: Shield, 
      tourId: "admin" 
    });
  }

  const checkScroll = () => {
    const element = navScrollRef.current;
    if (!element) return;
    const hasOverflow = element.scrollWidth > element.clientWidth;
    setShowLeftArrow(hasOverflow && element.scrollLeft > 5);
    setShowRightArrow(hasOverflow && element.scrollLeft < element.scrollWidth - element.clientWidth - 5);
  };

  useEffect(() => {
    const element = navScrollRef.current;
    if (element) {
      element.addEventListener('scroll', checkScroll);
    }
    window.addEventListener('resize', checkScroll);
    
    // Initial check after mount
    const timeoutId = setTimeout(checkScroll, 0);
    
    return () => {
      if (element) {
        element.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  const scroll = (direction) => {
    if (navScrollRef.current) {
      const scrollAmount = 200;
      navScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav
      className={`border-b sticky top-0 z-50 ${isLexUser ? "" : "bg-white border-gray-200"}`}
      style={isLexUser ? { background: "#0A1128", borderColor: "rgba(197,160,89,0.25)" } : undefined}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 gap-4">
          {/* Logo */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {isLexUser ? (
              <span className="text-xl font-semibold lex-serif text-white" data-testid="lex-nav-logo">
                lex<span style={{ color: '#C5A059' }}>.</span>ivoliq
              </span>
            ) : (
              <>
                <Calculator className="h-8 w-8 text-emerald-600" />
                <span className="text-xl font-bold text-gray-900">ivoliq</span>
              </>
            )}
          </div>
          
          {/* Desktop Navigation with Scroll */}
          <div className="hidden md:flex items-center flex-1 relative min-w-0">
            {showLeftArrow && (
              <button
                onClick={() => scroll('left')}
                className="absolute left-0 z-20 bg-white hover:bg-gray-50 shadow-lg rounded-full p-1.5 border border-gray-200"
                aria-label="Scroll left"
              >
                <ChevronLeft className="h-4 w-4 text-gray-700" />
              </button>
            )}
            
            <div 
              ref={navScrollRef}
              className="flex space-x-1 overflow-x-auto scrollbar-hide scroll-smooth w-full"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {navItems.map((item) => {
                const Icon = item.icon;
                // P1a: Top-Item ist auch aktiv, wenn aktuelle Route zu einer
                // Sub-Group des Items gehört (z.B. /projects → Kunden/Projekte aktiv).
                const groupItems = item.group ? SUB_NAVS[item.group] : null;
                const isActive = item.path === "/dashboard"
                  ? location.pathname.startsWith("/dashboard")
                  : groupItems
                  ? groupItems.some((g) => g.path === location.pathname)
                  : location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    data-tour={item.tourId}
                    className={`flex items-center space-x-2 px-3 py-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                      isLexUser
                        ? isActive
                          ? "text-[#C5A059] bg-white/5 border-b-2 border-[#C5A059] -mb-px"
                          : "text-slate-300 hover:bg-white/5 hover:text-white"
                        : isActive
                        ? "rounded-lg bg-emerald-50 text-emerald-700"
                        : item.locked
                        ? "rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-500"
                        : "rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                    {item.locked && <Lock className="h-3.5 w-3.5 text-gray-400" />}
                    {item.path === "/finances" && missingReceipts > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-xs font-bold bg-amber-500 text-white rounded-full" data-testid="nav-receipt-badge">
                        {missingReceipts > 9 ? "9+" : missingReceipts}
                      </span>
                    )}
                    {item.path === "/steuerberater-bereich" && sbUnreadCount > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-xs font-bold bg-blue-600 text-white rounded-full" data-testid="nav-sb-badge">
                        {sbUnreadCount > 9 ? "9+" : sbUnreadCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
            
            {showRightArrow && (
              <button
                onClick={() => scroll('right')}
                className="absolute right-0 z-20 bg-white hover:bg-gray-50 shadow-lg rounded-full p-1.5 border border-gray-200"
                aria-label="Scroll right"
              >
                <ChevronRight className="h-4 w-4 text-gray-700" />
              </button>
            )}
          </div>

          {/* User Info & Mobile menu button */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {user && (
              <>
                {/* Feb 2026 — Sprint 1 D2: Was-ist-neu? Changelog (nicht für lex-User — ivoliq-fokussiert) */}
                {!isLexUser && <ChangelogButton />}
                <button
                  onClick={logout}
                  data-testid="logout-btn"
                  className={`hidden md:flex items-center gap-2 px-3 py-2 transition-colors ${
                    isLexUser ? "text-slate-300 hover:bg-white/10 hover:text-white" : "rounded-lg text-gray-600 hover:bg-gray-100"
                  }`}
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </>
            )}
            
            {/* Language switcher removed - DE only for Go-Live 1.0 (EN planned for v1.1) */}

            <button
              data-testid="mobile-menu-button"
              className={`md:hidden p-2 ${isLexUser ? "text-white hover:bg-white/10" : "rounded-lg hover:bg-gray-100"}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const groupItems = item.group ? SUB_NAVS[item.group] : null;
              const isActive = groupItems
                ? groupItems.some((g) => g.path === location.pathname)
                : location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  data-tour={item.tourId}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 transition-colors ${
                    isLexUser
                      ? isActive
                        ? "text-[#C5A059] bg-white/5"
                        : "text-slate-300 hover:bg-white/5"
                      : isActive
                      ? "rounded-lg bg-emerald-50 text-emerald-700"
                      : item.locked
                      ? "rounded-lg text-gray-400 hover:bg-gray-50"
                      : "rounded-lg text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.locked && <Lock className="h-3.5 w-3.5 text-gray-400" />}
                </Link>
              );
            })}
            
            {/* Mobile User Info */}
            {user && (
              <>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 ${
                    isLexUser ? "text-slate-300 hover:bg-white/5" : "rounded-lg text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {/* lex (Sep 2026): Header-Hinweis — Einrichtung übersprungen, später fertigstellen */}
      {isLexUser && user && !user.onboarding_completed && user.lex_onboarding_skipped && (
        <div className="w-full" style={{ background: "#C5A059" }} data-testid="lex-onboarding-hint-banner">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs font-semibold" style={{ color: "#0A1128" }}>
              Ihre Einrichtung ist noch unvollständig — Honorarnoten und UVA starten erst nach Abschluss.
            </p>
            <button onClick={resumeLexOnboarding}
              className="text-xs font-bold uppercase tracking-wider px-3 py-1 transition-opacity hover:opacity-90"
              style={{ background: "#0A1128", color: "#C5A059" }}
              data-testid="lex-onboarding-resume-btn">
              Jetzt fertigstellen
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

function AppModals() {
  const { 
    showTaxResidencyModal, 
    showLocationChangeModal,
    detectedCountry,
    locationCheckData,
    handleTaxResidencyConfirm,
    handleLocationUpdate,
    handleLocationChangeDismiss,
    user,
    loading,
    refreshUser
  } = useAuth();

  // Unified onboarding: covers both tax residency AND setup
  // Must wait for loading=false to prevent flicker during auth checks
  const needsOnboarding = !loading
    && user 
    && (!user.onboarding_completed || !user.tax_residency_confirmed)
    && !showLocationChangeModal;

  // Determine start step: if tax residency not confirmed, start at country (0), otherwise business (1)
  const wizardStartStep = user && !user.tax_residency_confirmed ? 0 : 1;

  // Show TaxResidencyModal ONLY if onboarding IS completed but tax residency not confirmed
  // (edge case: existing user whose onboarding was already done)
  const showOldTaxModal = showTaxResidencyModal && user?.onboarding_completed && !needsOnboarding;

  // lex.ivoliq: Anwälte durchlaufen den lex-Onboarding-Wizard statt des Standard-Wizards
  const isLexUser = isLexUserFn(user);

  return (
    <>
      {showOldTaxModal && (
        <TaxResidencyModal
          isOpen={true}
          onConfirm={handleTaxResidencyConfirm}
          detectedCountry={detectedCountry}
          userEmail={user?.email}
        />
      )}
      <LocationChangeModal
        isOpen={showLocationChangeModal}
        onClose={handleLocationChangeDismiss}
        onUpdate={handleLocationUpdate}
        currentCountry={locationCheckData?.current_tax_country}
        detectedCountry={locationCheckData?.detected_country}
      />
      {needsOnboarding && isLexUser && !user.lex_onboarding_skipped && (
        <LexOnboardingWizard onComplete={() => refreshUser()} allowSkip />
      )}
      {needsOnboarding && !isLexUser && (
        <OnboardingWizard
          onComplete={() => refreshUser()}
          startAtStep={wizardStartStep}
          detectedCountry={detectedCountry || user?.country}
        />
      )}
      {/* Soft-gate AVV banner for existing users without acceptance */}
      {!needsOnboarding && !user?.is_guest && <AVVConsentBanner />}
      {/* Sep 2026 — Soft-Onboarding: Profil-vervollständigen-Hinweis */}
      {!needsOnboarding && !user?.is_guest && <ProfileCompletionBanner />}
      {/* Soft-Gate E-Mail-Verifizierung (Juni 2026, Paket A) */}
      {!needsOnboarding && !user?.is_guest && <EmailVerifyBanner />}
    </>
  );
}

// Smart root route component
function RootRoute() {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // QR-Code-Workaround (Iter 72 Feedback): Viele Hosts (z.B. dev.ivoliq.io) haben
  // keinen SPA-Fallback in nginx und liefern bei direktem Aufruf von /m/scan ein
  // 404. Der QR zeigt daher auf /?scan=1 — die Root-Route routet clientseitig
  // direkt zu /m/scan weiter. Damit funktioniert der Deep-Link überall.
  const params = new URLSearchParams(location.search);
  if (params.get('scan') === '1') {
    return <Navigate to="/m/scan" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }
  
  // If authenticated, redirect based on plan
  if (isAuthenticated) {
    if (isLexUserFn(user)) {
      // Strikte Produkt-Trennung (Sep 2026): lex-Session auf der Hauptdomain wird bereits
      // im AuthContext verworfen — hier NICHT cross-domain weiterleiten (User-Report),
      // nur kurz nichts rendern, bis der Auth-Check auflöst.
      if (lexHostUrl('/lex')) {
        return null;
      }
      return <Navigate to="/lex" replace />;
    }
    // Offener Einstieg (2026): Gäste starten in der Kachel-Auswahl "/willkommen"
    if (user?.is_guest) {
      return <Navigate to="/willkommen" replace />;
    }
    // Aug 2026 (Bugfix): FL-User auf lex-Host/-Modus bleibt auf der lex-Landing —
    // NIE auf die Hauptdomain weiterleiten (dev.lex.ivoliq.io → dev.ivoliq.io/dashboard war falsch).
    if (isLexMode(location.search)) {
      return <LexLanding />;
    }
    const targetPath = (!user?.plan || user.plan === 'free') ? '/time-tracking' : '/dashboard';
    return <Navigate to={targetPath} replace />;
  }

  // lex.ivoliq: eigene Landing auf lex-Hosts (oder ?lex=1 in Preview)
  if (isLexMode(location.search)) {
    return <LexLanding />;
  }

  return <NewLandingOptimized />;
}

// SB-Portal Subdomain-Routing (Juni 2026): sb.ivoliq.io / dev.sb.ivoliq.io.
// Auf der Hauptdomain werden SB-Routen (/steuerberater, /advisor/*) auf die
// Subdomain umgeleitet — auf der Subdomain alles andere zurück zur Hauptdomain.
// In Preview/localhost passiert nichts (siehe utils/sbDomain.js).
function DomainGuard() {
  const location = useLocation();
  useEffect(() => {
    // Strikte Produkt-Trennung (Sep 2026): lex-Pfade auf der Hauptdomain → lex-Host
    if (enforceLexDomain(location.pathname, location.search)) return;
    enforceSbDomain(location.pathname, location.search);
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <DomainGuard />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <MaintenanceWrapper />
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

function MaintenanceWrapper() {
  const { enabled, loading, message, eta } = useMaintenanceMode();

  // Show loading spinner while checking maintenance status
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Show maintenance page if maintenance mode is enabled
  if (enabled) {
    return <MaintenancePage maintenanceData={{ message, eta }} />;
  }

  // Show normal app if maintenance mode is disabled
  return (
    <>
      <ScrollToTop />
      <PageviewTracker />
      <InactivityWarningModal />
      <AppModals />
      <div className="App min-h-screen bg-gray-50">
        <Routes>
          {/* Root route - smart redirect based on auth status */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/design-preview" element={<DesignPreview />} />
          
          {/* P4 Mobile-Scan (Feb 2026): Vollbild-Page außerhalb MainLayout */}
          <Route path="/m/scan" element={<MobileScan />} />

          {/* Rückkehr-Magic-Link: unterbrochene Demo fortsetzen (Juni 2026) */}
          <Route path="/demo/fortsetzen/:token" element={<GuestResume />} />

          {/* Public Routes */}
          {/* SEO-Auftrag Block 4 (Aug 2026): /landing wird nicht mehr gebraucht → dauerhaft auf Startseite umleiten */}
          <Route path="/landing" element={<Navigate to="/" replace />} />
          <Route path="/new-landing" element={<NewLanding />} />
          <Route path="/freelancer-buchhaltung" element={<Navigate to="/buchhaltung-freelancer-oesterreich" replace />} />
          
          {/* SEO Landing Pages – saubere URLs (canonical) */}
          <Route path="/buchhaltung-freelancer-oesterreich"      element={<BuchhaltungOesterreich />} />
          <Route path="/buchhaltung-freelancer-deutschland"      element={<BuchhaltungDeutschland />} />
          <Route path="/buchhaltung-freelancer-schweiz"          element={<BuchhaltungSchweiz />} />
          <Route path="/lexoffice-alternative"                   element={<LexofficeAlternative />} />
          <Route path="/sevdesk-alternative"                     element={<SevdeskAlternative />} />
          <Route path="/rechnungsvorlage-freelancer"             element={<RechnungsvorlageFreelancer />} />
          <Route path="/steuerruecklagen-freelancer"             element={<SteuerruecklagenFreelancer />} />
          <Route path="/steuerruecklagen-freelancer-deutschland" element={<SteuerruecklagenDeutschland />} />
          <Route path="/steuerruecklagen-freelancer-oesterreich" element={<SteuerruecklagenOesterreich />} />
          <Route path="/steuerruecklagen-freelancer-schweiz"     element={<SteuerruecklagenSchweiz />} />
          {/* Primary Invoice Generator Pages */}
          <Route path="/rechnung-erstellen-kostenlos-deutschland" element={<InvoiceGeneratorDE />} />
          <Route path="/rechnung-erstellen-kostenlos-oesterreich" element={<InvoiceGeneratorAT />} />
          <Route path="/rechnung-erstellen-kostenlos-schweiz"     element={<Navigate to="/qr-rechnung-erstellen-kostenlos" replace />} />
          <Route path="/belegerfassung-freelancer"                 element={<BelegerfassungFreelancer />} />
          <Route path="/zeiterfassung-freelancer"                  element={<ZeiterfassungFreelancer />} />
          <Route path="/zeiterfassung-freelancer-oesterreich"      element={<ZeiterfassungOesterreich />} />
          <Route path="/zeiterfassung-rechnungsstellung"           element={<Navigate to="/zeiterfassung-freelancer#rechnung" replace />} />
          <Route path="/e-rechnung-freelancer"                    element={<ERechnungFreelancer />} />
          <Route path="/stundensatz-rechner"                       element={<StundensatzRechner />} />
          <Route path="/qr-rechnung-erstellen-kostenlos"           element={<QrRechnungGenerator />} />
          <Route path="/steuerberater-portal"                      element={<SteuerberaterPortal />} />
          {/* 301-Redirects: .html → saubere URL (SEO-Signal an Google) */}
          <Route path="/buchhaltung-freelancer-oesterreich.html" element={<Navigate to="/buchhaltung-freelancer-oesterreich" replace />} />
          <Route path="/buchhaltung-freelancer-deutschland.html" element={<Navigate to="/buchhaltung-freelancer-deutschland" replace />} />
          <Route path="/buchhaltung-freelancer-schweiz.html"     element={<Navigate to="/buchhaltung-freelancer-schweiz" replace />} />
          <Route path="/lexoffice-alternative.html"              element={<Navigate to="/lexoffice-alternative" replace />} />
          <Route path="/sevdesk-alternative.html"                element={<Navigate to="/sevdesk-alternative" replace />} />
          <Route path="/rechnungsvorlage-freelancer.html"        element={<Navigate to="/rechnungsvorlage-freelancer" replace />} />
          <Route path="/steuerruecklagen-freelancer.html"        element={<Navigate to="/steuerruecklagen-freelancer" replace />} />
          <Route path="/steuerruecklagen-freelancer-deutschland.html"  element={<Navigate to="/steuerruecklagen-freelancer-deutschland" replace />} />
          <Route path="/steuerruecklagen-freelancer-oesterreich.html"  element={<Navigate to="/steuerruecklagen-freelancer-oesterreich" replace />} />
          <Route path="/steuerruecklagen-freelancer-schweiz.html"      element={<Navigate to="/steuerruecklagen-freelancer-schweiz" replace />} />
          <Route path="/rechnung-erstellen-kostenlos-deutschland.html" element={<Navigate to="/rechnung-erstellen-kostenlos-deutschland" replace />} />
          <Route path="/rechnung-erstellen-kostenlos-oesterreich.html" element={<Navigate to="/rechnung-erstellen-kostenlos-oesterreich" replace />} />
          <Route path="/rechnung-erstellen-kostenlos-schweiz.html"     element={<Navigate to="/qr-rechnung-erstellen-kostenlos" replace />} />
          {/* Legacy URL Redirects */}
          <Route path="/rechnungsvorlage-freelancer-deutschland" element={<Navigate to="/rechnung-erstellen-kostenlos-deutschland" replace />} />
          <Route path="/rechnungsvorlage-freelancer-oesterreich" element={<Navigate to="/rechnung-erstellen-kostenlos-oesterreich" replace />} />
          <Route path="/rechnungsvorlage-freelancer-schweiz"     element={<Navigate to="/qr-rechnung-erstellen-kostenlos" replace />} />
          <Route path="/rechnung-erstellen-kostenlos"            element={<Navigate to="/rechnung-erstellen-kostenlos-deutschland" replace />} />
          
          {/* Sep 2026: Auf dem lex-Host führt /login (und Aliase) IMMER zum lex-Login, nie zur ivoliq-Anmeldung */}
          <Route path="/login" element={isLexHost() ? <Navigate to="/lex-login" replace /> : <Login />} />
          {isLexEnabled() ? (
            <Route path="/lex-login" element={<LexLogin />} />
          ) : (
            <Route path="/lex-login" element={<Navigate to="/" replace />} />
          )}
          {/* lex-Rechtstexte: immer erreichbar (auch im Landing-only-Modus) */}
          <Route path="/lex-impressum" element={<LexLegal section="impressum" />} />
          <Route path="/lex-datenschutz" element={<LexLegal section="datenschutz" />} />
          <Route path="/lex-agb" element={<LexLegal section="agb" />} />
          <Route path="/signup" element={isLexHost() ? <Navigate to="/lex-login" replace /> : <Login />} />
          <Route path="/register" element={isLexHost() ? <Navigate to="/lex-login" replace /> : <Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/aktivieren" element={<ActivateAccount />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/gmail-callback" element={<GmailCallback />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/upgrade-success" element={<UpgradeSuccess />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/avv" element={<AVV />} />
          <Route path="/steuerberater-agb" element={<SBTerms />} />
          <Route path="/steuerberater-avv" element={<SBAvv />} />
          <Route path="/tax-documentation" element={<TaxDocumentation />} />
          {/* Öffentliche Angebots-Annahme (kein Login nötig) */}
          <Route path="/angebot/:token" element={<QuoteAccept />} />
          {/* lex.ivoliq: öffentliche Honorarnoten-Freigabe (Token-Link aus der Mail) */}
          {isLexEnabled() && <Route path="/lex/freigabe/:token" element={<LexApprove />} />}
          
          {/* Advisor Portal Routes (standalone, no freelancer navigation) */}
          {/* Gemeinsamer Auth-Provider: EIN /me-Check für alle SB-Seiten →
              keine gegenseitige Login↔Dashboard-Oszillation mehr. */}
          <Route element={<AdvisorAuthProvider><Outlet /></AdvisorAuthProvider>}>
            <Route path="/steuerberater" element={<AdvisorLogin />} />
            <Route path="/advisor/dashboard" element={<AdvisorDashboard />} />
            {/* lex.ivoliq Kanzlei-Portal (Haus-StB) — bewusst NICHT unter /advisor/* (keine SB-Domain-Umleitung) */}
            {isLexEnabled() && <Route path="/advisor/lex" element={<Navigate to="/kanzlei" replace />} />}
            {isLexEnabled() ? (
              <Route path="/kanzlei" element={<KanzleiPortal />} />
            ) : (
              <Route path="/kanzlei" element={<Navigate to="/" replace />} />
            )}
            {isLexEnabled() && <Route path="/kanzlei/mandant/:clientId" element={<AdvisorClientDetail />} />}
            <Route path="/advisor/abrechnung" element={<AdvisorBilling />} />
            <Route path="/advisor/client/:clientId" element={<AdvisorClientDetail />} />
          </Route>
          
          {/* Protected App Routes */}
          {isLexEnabled() ? (
            <Route path="/lex" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
          ) : (
            <Route path="/lex" element={<Navigate to="/" replace />} />
          )}
          {isLexEnabled() && <Route path="/lex/honorar" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />}
          <Route path="/dashboard/*" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/dashboard-legacy" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/dashboard-v2" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/customers" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/willkommen" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/projects" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/standard-articles" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/time-tracking" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/finances" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/migration" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          {/* Feb 2026 — Iter-73-Backlog: ExpenseDetail als echte Route für Deep-Links aus SB-Kommentaren / Inbox-Widget */}
          <Route path="/expense/:expenseId" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/banking" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/anlagen" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/reisen" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/voranmeldung" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/exports" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/invoices" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/quotes" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/recurring" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/taxes" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/steuerberater-bereich" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
        </Routes>
        <Toaster position="top-right" />
        <CookieBanner />
        <TimerReminder />
      </div>
    </>
  );
}

function MainLayout() {
  const location = useLocation();
  const { user } = useAuth();
  const isLexUser = isLexUserFn(user);

  // Aug 2026 — Produkt-Trennung ivoliq/lex (Bugfix: dev.lex.ivoliq.io/taxes zeigte die FL-App):
  // 1) lex-User dürfen nur lex-Routen (+ Voranmeldung/Einstellungen) sehen → sonst /lex.
  // 2) FL-User im lex-Modus/-Host gehören auf die Hauptdomain (Preview: lex-Modus beenden).
  const LEX_ALLOWED_PATHS = ['/voranmeldung', '/settings'];
  if (isLexUser) {
    // Strikte Produkt-Trennung (Sep 2026): lex-Session auf der Hauptdomain wird im
    // AuthContext verworfen — hier nur nichts rendern, bis der Auth-Check auflöst.
    if (lexHostUrl('/lex')) {
      return null;
    }
    if (!location.pathname.startsWith('/lex') && !LEX_ALLOWED_PATHS.includes(location.pathname)) {
      return <Navigate to="/lex" replace />;
    }
  }
  if (user && !isLexUser && isLexMode()) {
    // Aug 2026 (Bugfix): FL-User auf lex-Host NIE auf die Hauptdomain umleiten —
    // stattdessen zur lex-Landing auf derselben Domain. In Preview: lex-Modus beenden.
    if (isLexHost()) {
      return <Navigate to="/" replace />;
    }
    clearLexMode();
  }

  // Determine which page component to render based on route
  let PageComponent = DashboardLayout; // default

  if (location.pathname === '/lex') PageComponent = LexDashboard;
  else if (location.pathname === '/lex/honorar') PageComponent = LexHonorar;
  else if (location.pathname === '/dashboard-legacy') PageComponent = DashboardLayout;
  else if (location.pathname === '/dashboard-v2') PageComponent = DashboardV2;
  else if (location.pathname.startsWith('/dashboard')) PageComponent = Dashboard;
  else if (location.pathname === '/customers') PageComponent = Customers;
  else if (location.pathname === '/willkommen') PageComponent = GuestWelcome;
  else if (location.pathname === '/projects') PageComponent = Projects;
  else if (location.pathname === '/standard-articles') PageComponent = StandardArticlesPage;
  else if (location.pathname === '/time-tracking') PageComponent = TimeTracking;
  else if (location.pathname === '/finances') PageComponent = Finances;
  else if (location.pathname === '/migration') PageComponent = Migration;  // Feb 2026 — Deep-Link Route /expense/:id öffnet Finances + Auto-Open des Belegs (via URL-Param)
  else if (location.pathname.startsWith('/expense/')) PageComponent = Finances;
  else if (location.pathname === '/banking') PageComponent = Banking;
  else if (location.pathname === '/anlagen') PageComponent = Anlagen;
  else if (location.pathname === '/reisen') PageComponent = Travel;
  else if (location.pathname === '/voranmeldung') PageComponent = Voranmeldung;
  else if (location.pathname === '/exports') PageComponent = Exports;
  else if (location.pathname === '/invoices') PageComponent = Invoices;
  else if (location.pathname === '/quotes') PageComponent = Quotes;
  else if (location.pathname === '/recurring') PageComponent = Recurring;
  else if (location.pathname === '/taxes') PageComponent = Taxes;
  else if (location.pathname === '/steuerberater-bereich') PageComponent = SteuerberaterTab;
  else if (location.pathname === '/settings') PageComponent = Settings;
  else if (location.pathname === '/admin') PageComponent = Admin;

  // Demo-Modus (Gast): Steuerberater/Einstellungen sowie Angebote/Retainer/Standardartikel
  // sind NICHT funktional → Info-Sperrseite. Finanzen & Steuern sind read-only GEÖFFNET
  // (echte Demo-Daten sichtbar, USP-Aha), Writes blockt das Backend (guest_readonly).
  const guestLockArea = user?.is_guest
    ? (location.pathname === '/steuerberater-bereich' ? 'advisor'
      : location.pathname === '/settings' ? 'settings'
      : location.pathname === '/quotes' ? 'quotes'
      : location.pathname === '/recurring' ? 'recurring'
      : location.pathname === '/standard-articles' ? 'articles'
      : location.pathname === '/anlagen' ? 'anlagen'
      : location.pathname === '/reisen' ? 'reisen'
      : location.pathname === '/banking' ? 'banking'
      : location.pathname === '/voranmeldung' ? 'voranmeldung'
      : location.pathname === '/exports' ? 'exports'
      : null)
    : null;
  const guestReadOnly = !!user?.is_guest && (
    ['/finances', '/taxes'].includes(location.pathname)
    || location.pathname.startsWith('/expense/')
  );

  return (
    <div className={`min-h-screen flex flex-col ${isLexUser ? 'lex-theme' : ''}`}>
      <GuestBanner />
      <AreaGuide />
      <Navigation />
      {!isLexUser && <GlobalTimerBar />}
      <SubNav />
      {guestReadOnly && <GuestReadOnlyBanner />}
      <div className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {guestLockArea ? <GuestAreaLock area={guestLockArea} /> : <PageComponent />}
      </div>
      {isLexUser ? <LexAppFooter /> : <Footer />}
      {/* Floating Feedback Button - only show in authenticated areas */}
      <FloatingFeedbackButton />
      {!isLexUser && <MigrationHintModal />}
    </div>
  );
}

export default App;
export { API, toast };
